import { useState } from "react";
import { Apis } from "../../lib/Apis";
import Swal from "sweetalert2";

// --- Hook from Brands/useHook.js ---
export const useBrandHook = () => {
  const getApiError = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

  const addBrand = async (data) => {
    try {
      const response = await Apis.addBrand(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add brand.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to add brand."));
    }
  };

  const getBrands = async (setBrands) => {
    try {
      const response = await Apis.getBrands();
      const payload = response?.data?.data;
      const brands = payload?.brands || (Array.isArray(payload) ? payload : []);
      if (setBrands) {
        setBrands(brands);
      }
      return brands;
    } catch (error) {
      if (setBrands) setBrands([]);
      throw new Error(getApiError(error, "Failed to fetch brands."));
    }
  };

  const updateBrand = async (brandId, data) => {
    try {
      const response = await Apis.updateBrand(brandId, data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update brand.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to update brand."));
    }
  };

  const deleteBrand = async (brandId) => {
    try {
      const response = await Apis.deleteBrand(brandId);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete brand.");
      }
      return body;
    } catch (error) {
      throw new Error(getApiError(error, "Failed to delete brand."));
    }
  };

  return {
    addBrand,
    getBrands,
    updateBrand,
    deleteBrand,
  };
};

// --- Hook from CarouselProducts/useHook.js ---
export const useBannerHook = () => {
  const addBanner = async (data) => {
    try {
      const response = await Apis.addBanner(data);
      return response;
    } catch (error) {
      return error;
    }
  };

  const getBanners = async (setBanners) => {
    try {
      const response = await Apis.getBanners();
      if (setBanners && response?.data) {
        setBanners(response.data);
      }
      return response;
    } catch (error) {
      return error;
    }
  };

  const updateBanner = async (bannerId, data) => {
    try {
      // Ensure you have Apis.updateBanner in your Apis file
      const response = await Apis.updateBanner(bannerId, data);
      return response;
    } catch (error) {
      return error;
    }
  };

  const deleteBanner = async (bannerId) => {
    try {
      const response = await Apis.deleteBanner(bannerId);
      return response;
    } catch (error) {
      return error;
    }
  };

  return {
    addBanner,
    getBanners,
    updateBanner,
    deleteBanner,
  };
};

// --- Hook from Categories/CategoriesDetails/useHook.js ---
export const useProductDetails = () => {
    const getProductDetails = async (id, setProductDetails) => {
        try {
            const response = await Apis.getProductDetails(id);
            const payload = response?.data?.data ?? response?.data;
            setProductDetails(payload || null);
            return response.data;
        } catch (error) {
            console.error("Error fetching product details: ", error);
            setProductDetails(null);
            throw error;
        }
    }
    return { getProductDetails };
};

// --- Hook from Categories/CategoriesList/useHook.js ---
export const useCategory = () => {
  const [loading, setLoading] = useState(false);

  const getApiErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

  const addCategory = async (categoryData) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", categoryData.name);
      if (categoryData.categoryName_ur) {
        formData.append("categoryName_ur", categoryData.categoryName_ur);
      }
      formData.append("status", categoryData.status);

      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }
      if (categoryData.image_alt) {
        formData.append("image_alt", categoryData.image_alt);
      }
      if (categoryData.image_title) {
        formData.append("image_title", categoryData.image_title);
      }

      const response = await Apis.addCategory(formData);
      if (response?.data?.success || response?.status === 201) {
        return response.data;
      }
      throw new Error(response?.data?.message || "Failed to add category.");
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Error adding category."));
    } finally {
      setLoading(false);
    }
  };
  const editCategory = async (id, categoryData) => {
    try {
      const formData = new FormData();
      formData.append("name", categoryData.name);
      if (categoryData.categoryName_ur) {
        formData.append("categoryName_ur", categoryData.categoryName_ur);
      }
      formData.append("status", categoryData.status);

      if (categoryData.image instanceof File) {
        formData.append("image", categoryData.image);
      }
      if (categoryData.image_alt !== undefined) {
        formData.append("image_alt", categoryData.image_alt || "");
      }
      if (categoryData.image_title !== undefined) {
        formData.append("image_title", categoryData.image_title || "");
      }

      const response = await Apis.updateCategoryData(id, formData);
      if (response?.data?.success || response?.status === 200) {
        return response.data;
      }
      throw new Error(response?.data?.message || "Failed to edit category.");
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Error editing category."));
    }
  };
  const getCategories = async (setCategories) => {
    setLoading(true);
    try {
      const response = await Apis.getCategories();
      const payload = response?.data?.data;
      if (response?.data?.success || response?.status === 200) {
        setCategories(payload?.categories || []);
        return response.data;
      }
      throw new Error(response?.data?.message || "Failed to fetch categories.");
    } catch (error) {
      alert(getApiErrorMessage(error, "Error fetching categories."));
    } finally {
      setLoading(false);
    }
  };

  const getCategoriesWithPagination = async (setCategories, setTotal, offset = 0, limit = 10, categoryName = "", status = "") => {
    setLoading(true);
    try {
      const params = {};

      if (categoryName) {
        params.categoryName = categoryName;
      }

      if (status !== "") {
        params.status = status === "true" ? true : false;
      }

      const response = await Apis.getCategoriesWithPagination(offset, limit, params);
      const payload = response?.data?.data;

      if (response?.data?.success || response?.status === 200) {
        const categories = payload?.categories || [];
        setCategories(categories);
        const totalRecords = payload?.pagination?.totalRecords || categories.length;
        setTotal(totalRecords);
        return response.data;
      }
      throw new Error(response?.data?.message || "Failed to fetch categories.");
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
      setTotal(0);
      throw new Error(getApiErrorMessage(error, "Error fetching categories."));
    } finally {
      setLoading(false);
    }
  };

  const searchCategories = async (categoryName, status, offset = 0, limit = 10) => {
    try {
      const params = {};

      if (categoryName) {
        params.categoryName = categoryName;
      }

      if (status !== "") {
        params.status = status === "true" ? true : false;
      }

      const response = await Apis.getCategoriesWithPagination(offset, limit, params);
      return response?.data?.data || { categories: [], pagination: { totalRecords: 0 } };
    } catch (error) {
      console.error("🚀 ~ file: useHook.js ~ line 64 ~ searchCategories ~ error", error);
      return { categories: [], pagination: { totalRecords: 0 } };
    }
  };

  const updateCategoryStatus = async (id, status) => {
    try {
      setLoading(true);
      const data = { id, status };
      const response = await Apis.updateCategoryStatus(id, data);
      if (response?.data?.success || response?.status === 200) {
        return response.data;
      }
      throw new Error(response?.data?.message || "Failed to update category status.");
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Error updating category status."));
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    try {
      setLoading(true);
      const response = await Apis.deleteCategory(id);
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Error deleting category."));
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryPosition = async (categoryId, newPosition) => {
    try {
      const response = await Apis.updateCategoryPosition({ categoryId, newPosition });
      return response?.data;
    } catch (error) {
      console.error("Error updating category position:", error);
      throw error;
    }
  };

  return {
    addCategory,
    editCategory,
    loading,
    getCategories,
    getCategoriesWithPagination,
    updateCategoryStatus,
    searchCategories,
    deleteCategory,
    updateCategoryPosition
  };
};

// --- Hook from Categories/CategoriesView/useHook.js ---
export const useCategoryProducts = () => {
    const getCategoryProducts = async (id, setCategoryProducts) => {
        try {
            const response = await Apis.getCategoryProducts(id);
            const payload = response?.data?.data;
            if (payload && Array.isArray(payload.products)) {
                setCategoryProducts(payload.products);
            } else {
                console.error("Invalid response format:", response);
                setCategoryProducts([]);
            }
            return payload;
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
            setCategoryProducts([]);
            throw error;
        }
    };

    // New paginated version of getCategoryProducts
    const getCategoryProductsWithPagination = async (id, setCategoryProducts, setTotal, offset = 0, limit = 10, name = "", status = "") => {
        try {
            const params = {};
            
            if (name) {
                params.name = name;
            }
            
            if (status !== "") {
                params.status = status === "true" ? true : false;
            }

            const response = await Apis.getCategoryProductsWithPagination(id, offset, limit, params);
            const payload = response?.data?.data || {};
            if (response?.data?.success || response?.status === 200) {
                const products = payload.products || [];
                setCategoryProducts(products);
                // Extract total from pagination object
                const totalRecords = payload.pagination?.totalRecords || products.length;
                setTotal(totalRecords);
                return response.data;
            }
            throw new Error(response?.data?.message || "Failed to fetch category products.");
        } catch (error) {
            console.error("Error fetching category products:", error);
            setCategoryProducts([]);
            setTotal(0);
            throw new Error(error?.response?.data?.message || error.message || "Error fetching category products.");
        }
    };

    const searchCategoryProducts = async (id, name, status, offset = 0, limit = 10) => {
        try {
            const params = {};
            
            if (name) {
                params.name = name;
            }
            
            if (status !== "") {
                params.status = status === "true" ? true : false;
            }

            const response = await Apis.getCategoryProductsWithPagination(id, offset, limit, params);
            return response?.data?.data || { products: [], pagination: { totalRecords: 0 } };
        } catch (error) {
            console.error("🚀 ~ file: useHook.js ~ line 64 ~ searchCategoryProducts ~ error", error);
            return { products: [], pagination: { totalRecords: 0 } };
        }
    };

    return {
        getCategoryProducts,
        getCategoryProductsWithPagination,
        searchCategoryProducts
    };
};

// --- Hook from DeliveryManagement/useHook.js ---
export const useDeliveryManagement = () => {
    const getInStationZones = async (setZoneData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllInStationZone(offset, limit);
            const payload = response.data?.data || {};
            setZoneData(payload.zones || []);
            return payload;
        } catch (error) {
            console.error('Error fetching zones:', error);
            throw error;
        }
    };
    const getOutStationZones = async (setZoneData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllZone(offset, limit);
            const payload = response.data?.data || {};
            setZoneData(payload.zones || []);
            return payload;
        } catch (error) {
            console.error('Error fetching zones:', error);
            throw error;
        }
    };
    const getAllInStationZones = async (setAllZoneData) => {
        try {
            const response = await Apis.getAllInStationZones();
            const payload = response.data?.data || {};
            setAllZoneData(payload.instationZones || []);
            return payload;
        } catch (error) {
            console.error('Error fetching zone:', error);
            throw error;
        }
    };
    const getAllOutStationZones = async (setAllZoneData) => {
        try {
            const response = await Apis.getZones();
            const payload = response.data?.data || {};
            setAllZoneData(payload.zones || []);
            return payload;
        } catch (error) {
            console.error('Error fetching zone:', error);
            throw error;
        }
    };
    const getAllInStationBaseRates = async (setAllInStationBaseRatesData) => {
        try {
            const response = await Apis.getAllInStation();
            const payload = response.data?.data || {};
            setAllInStationBaseRatesData(payload.inStationBaseRates || []);
            return payload;
        } catch (error) {
            console.error('Error fetching in station base rates:', error);
            throw error;
        }
    };
    const addInStationZone = async (data) => {
        try {
            const response = await Apis.createInStationZone(data);
            return response.data;
        } catch (error) {
            console.error('Error creating zone:', error);
            throw error;
        }
    };
    const addOutStationZone = async (data) => {
        try {
            const response = await Apis.createZone(data);
            return response.data;
        } catch (error) {
            console.error('Error creating zone:', error);
            throw error;
        }
    };
    const updateInStationZone = async (id, data) => {
        try {
            const response = await Apis.updateInStationZone(id, data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const updateOutStationZone = async (id, data) => {
        try {
            const response = await Apis.updateZone(id, data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteInStationZone = async (id) => {
        try {
            const response = await Apis.deleteInStationZone(id);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteOutStationZone = async (id) => {
        try {
            const response = await Apis.deleteZone(id);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const addCities = async (data) => {
        try {
            const response = await Apis.createCity(data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getCities = async (setCities, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getCities(offset, limit);
            const payload = response.data?.data || {};
            setCities(payload.cities || []);
            return payload;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteCity = async (id) => {
        try {
            const response = await Apis.deleteCity(id);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const updateCity = async (id, data) => {
        try {
            const response = await Apis.updateCityStatus(id, data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getInStationBaseRates = async (setInStationBaseRatesData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllInStation(offset, limit);
            const payload = response.data?.data || {};
            setInStationBaseRatesData(payload.inStationBaseRates || []);
            return payload;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getOutStationBaseRates = async (setOutStationBaseRatesData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllOutStationBaseRates(offset, limit);
            const payload = response.data?.data || {};
            setOutStationBaseRatesData(payload.outStationBaseRates || []);
            return payload;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const addInStationBaseRate = async (data) => {
        try {
            const response = await Apis.createInStationBaseRate(data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const addOutStationBaseRate = async (data) => {
        try {
            const response = await Apis.createOutStationBaseRate(data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const updateInStationBaseRate = async (id, data) => {
        try {
            const response = await Apis.updateInStationBaseRate(id, data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const updateOutStationBaseRate = async (id, data) => {
        try {
            const response = await Apis.updateOutStationBaseRate(id, data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteInStationBaseRate = async (id) => {
        try {
            const response = await Apis.deleteInStationBaseRate(id);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const deleteOutStationBaseRate = async (id) => {
        try {
            const response = await Apis.deleteOutStationBaseRate(id);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return {
        addCities,
        getCities,
        updateCity,
        deleteCity,
        getInStationZones,
        getAllInStationZones,
        addInStationZone,
        updateInStationZone,
        deleteInStationZone,
        getOutStationZones,
        getAllOutStationZones,
        addOutStationZone,
        updateOutStationZone,
        deleteOutStationZone,
        getInStationBaseRates,
        getOutStationBaseRates,
        addInStationBaseRate,
        addOutStationBaseRate,
        updateInStationBaseRate,
        updateOutStationBaseRate,
        deleteInStationBaseRate,
        deleteOutStationBaseRate,
        getAllInStationBaseRates
    };
};

// --- Hook from Orders/VerifyPayments/useHook.js ---
export const useOrderPayments = () => {
  const getPayment = async (status) => {
    try {
      const response = await Apis.getPayments(status);
      const payload = response?.data?.data || response?.data || {};
      return {
        ...response,
        data: payload,
      };
    } catch (error) {
      console.error("Error fetching payment data:", error);
      throw error;
    }
  };

  const verifyPayment = async (id, status) => {
    try {
      const body = { paymentStatus: status };
      const response = await Apis.updatePaymentStatus(id, body);
      return response;
    } catch (error) {
      throw error;
    }
  };

  return { verifyPayment, getPayment };
};

// --- Hook from ProductDeliveryCharges/useHook.js ---
export const useDeliveryCharges = () => {
    const getCities = async (setCities) => {
        try {
            const response = await Apis.getCities();
            const payload = response.data?.data;
            setCities(payload?.cities || (Array.isArray(payload) ? payload : []));
        } catch (error) {
            console.error(error);
        }
    };
    const getProducts = async (setProducts) => {
        try {
            const response = await Apis.getProductsForDeliveryCharges();
            const products = response?.data?.data?.products;
            if (Array.isArray(products)) {
                setProducts(products);
            } else {
                setProducts([]);
            }
        } catch (error) {
            console.error(error);
            setProducts([]);
        }
    };
    const getProductNameList = async (setProducts) => {
        try {
            const response = await Apis.getProductNameList();
            const payload = response?.data?.data;
            setProducts(payload?.products || (Array.isArray(payload) ? payload : []));
        } catch (error) {
            console.error(error);
            setProducts([]);
        }
    }
    const addProductDeliveryCharges = async (data) => {
        try {
            const response = await Apis.addProductDeliveryCharges(data);
            return response;
        } catch (error) {
            console.error(error);
        }
    }
    const getProductDeliveryCharges = async (setDeliveryCharges, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getProductsDeliveryCharges(offset, limit);
            setDeliveryCharges(response.data.data);
            return response.data;
        } catch (error) {
            console.error(error);
            setDeliveryCharges([]);
        }
    };
    const deleteProductDeliveryCharges = async (id) => {
        try {
            const response = await Apis.deleteProductDeliveryCharges(id);
            return response;
        } catch (error) {
            console.error(error);
        }
    }
    const editProductDeliveryCharges = async (id, data) => {
        try {
            const response = await Apis.updateProductDeliveryCharges(id, data);
            return response;
        } catch (error) {
            console.error(error);
        }
    }
    return { getCities, getProducts, getProductDeliveryCharges, addProductDeliveryCharges, deleteProductDeliveryCharges, editProductDeliveryCharges, getProductNameList };
}

// --- Hook from Products/ProductList/useHook.js ---
export const useProducts = () => {
    const [loading, setLoading] = useState(false);
    const getProducts = async (setProducts) => {
        try {
            const response = await Apis.getProducts();
            const payload = response?.data?.data;
            if (payload && Array.isArray(payload.products)) {
                setProducts(payload.products);
            } else {
                console.error("Invalid response format:", response);
            }
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
        }
    };

    const getProductsWithPagination = async (setProducts, setTotal, offset = 0, limit = 20, searchQuery = "", statusFilter = "", categoryName = "") => {
        setLoading(true);
        try {
            const params = {};

            if (statusFilter !== "") {
                params.status = statusFilter === "true" ? true : false;
            }

            if (searchQuery && searchQuery.trim()) {
                params.name = searchQuery.trim();
            }

            if (categoryName && categoryName.trim()) {
                params.categoryName = categoryName.trim();
            }

            const response = await Apis.getProductsWithPagination(offset, limit, params);
            if (response?.status === 200) {
                const payload = response?.data?.data || {};
                const products = payload.products || [];
                setProducts(products);
                const totalRecords = payload.pagination?.totalRecords || products.length;
                setTotal(totalRecords);
                return payload;
            } else {
                throw new Error(response?.data?.message || "Failed to fetch products.");
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            throw new Error(error?.response?.data?.message || error.message || "Error fetching products.");
        } finally {
            setLoading(false);
        }
    };

    const getCategories = async (setCategories) => {
        try {
            const response = await Apis.getCategoriesToAddProducts();
            const payload = response?.data?.data;
            if (
                payload &&
                Array.isArray(payload.categories)
            ) {
                setCategories(payload.categories);
            } else {
                console.error("Invalid response format:", response);
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error?.message || error);
            setCategories([]);
        }
    };
    const updateProductStatus = async (id, status) => {
        try {
            setLoading(true);
            const requestBody = {
                id,
                status,
            };
            const response = await Apis.updateProductStatus(requestBody);
            if (response && response.data?.success) {
                return response.data;
            } else {
                console.error("Invalid response format:", response);
                return null;
            }
        } catch (error) {
            console.error("Error in Hook:", error?.response?.data?.message || error.message || error);
            throw new Error(error?.response?.data?.message || "Failed to update product status.");
        } finally {
            setLoading(false);
        }
    };
    const deleteProduct = async (id) => {
        try {
            const response = await Apis.deletProduct(id);
            if (response && response.data?.success) {
                return response.data;
            } else {
                console.error("Invalid response format:", response);
                return null;
            }
        } catch (error) {
            console.error("Error in Hook:", error?.response?.data?.message || error.message || error);
            throw new Error(error?.response?.data?.message || "Failed to delete product.");
        }
    };
    const addProduct = async (formData) => {
        try {
            const response = await Apis.addProduct(formData);
            return response.data;
        } catch (error) {
            console.error(error);
            throw new Error(error?.response?.data?.message || error.message || "Failed to add product.");
        }
    };
    const updateProduct = async (updateId, data) => {
        try {
            const response = await Apis.updateProduct(updateId, data);
            return response?.data;
        } catch (error) {
            console.error(error);
            throw new Error(error?.response?.data?.message || error.message || "Failed to update product.");
        }
    };
    const getBrands = async (setBrandsData) => {
        try {
            const response = await Apis.getBrands();
            const brands = response?.data?.data?.brands || [];
            setBrandsData(brands);
            return brands;
        } catch (error) {
            throw new Error(error?.response?.data?.message || error.message || "Failed to fetch brands.");
        }
    };

    const updateProductPosition = async (productId, newPosition) => {
        try {
            const response = await Apis.updateProductPosition(productId, newPosition);
            return response?.data ?? response;
        } catch (error) {
            console.error("Error in Hook:", error?.response?.data?.message || error.message || error);
            throw new Error(error?.response?.data?.message || "Failed to update product position.");
        }
    };

    return {
        getProducts,
        getProductsWithPagination,
        getCategories,
        updateProductStatus,
        deleteProduct,
        addProduct,
        updateProduct,
        getBrands,
        updateProductPosition
    };
};

// --- Hook from Products/ProductView/useHook.js ---
export const useDiscountTier = () => {
    const isOk = (response) =>
        response?.data?.success === true || response?.status === 200 || response?.status === 201;

    const getDiscountTiers = async (productId, setDiscountTiers) => {
        try {
            const response = await Apis.getDiscountedProducts(productId);
            const payload = response?.data?.data;
            setDiscountTiers(Array.isArray(payload) ? payload : []);
            return response.data;
        } catch (error) {
            setDiscountTiers([]);
            return error;
        }
    };
    const addDiscountTiers = async (productId, newDiscount) => {
        try {
            const payload = {
                productId,
                discountTiers: [newDiscount]
            };
            const response = await Apis.addDiscountedProducts(payload);
            return response?.data;
        } catch (error) {
            throw error;
        }
    };
    const delteDiscountTiers = async (productId, discountTierId) => {
        try {
            const response = await Apis.deleteDiscountedProducts(productId, discountTierId);
            return response?.data;
        } catch (error) {
            throw error;
        }
    };
    const showOnHomeScreen = async (productId, homePage) => {
        try {
            const body = {
                productId,
                homePage: homePage === 1 ? 0 : 1,
            };
            const response = await Apis.showOnHomeScreen(body);
            if (isOk(response)) {
                Swal.fire({
                    title: "Success!",
                    text: `The product has been successfully ${homePage === 1 ? "removed from" : "added to"} the home screen.`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
            }
            return response?.data;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "There was an issue updating the product on the home screen.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return error;
        }
    };
    const getProductData = async (productId, setProductData) => {
        try {
            const response = await Apis.getProductById(productId);
            const payload = response?.data?.data ?? response?.data;
            setProductData(payload);
            return payload?.homeScreen;
        } catch (error) {
            return error;
        }
    };
    const addStock = async (productId, stock) => {
        try {
            const response = await Apis.addStock({ productId, stock });
            if (isOk(response)) {
                Swal.fire({
                    title: "Success!",
                    text: response?.data?.message || "Stock has been added successfully.",
                    icon: "success",
                    confirmButtonText: "OK",
                });
            }
            return response?.data;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.message || "Failed to update stock.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return error;
        }
    };
    const showProductOnHomeScreen = async (productId, homePage) => {
        try {
            const response = await Apis.showOnHomeScreen({
                productId,
                homePage: homePage ? 1 : 0
            });
            return response?.data;
        } catch (error) {
            console.error("Error updating home screen status:", error);
            return error;
        }
    };
    const getCategories = async (setCategories) => {
        try {
            const response = await Apis.getCategoriesToAddProducts();
            setCategories(response?.data?.data?.categories || []);
        } catch (error) {
            setCategories([]);
            return error;
        }
    };
    const updateVariantStock = async (colorId, inStock) => {
        try {
            const response = await Apis.updateVariantStock({ colorId, inStock });
            if (isOk(response)) {
                Swal.fire({
                    title: "Success!",
                    text: response?.data?.message || "Variant stock status updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
            return response?.data;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to update variant stock status.",
                icon: "error",
                timer: 2000,
                showConfirmButton: false,
            });
            return error;
        }
    };
    const updateVariantStockDetails = async (data) => {
        try {
            const response = await Apis.updateVariantStock(data);
            if (isOk(response)) {
                Swal.fire({
                    title: "Success!",
                    text: response?.data?.message || "Variant stock details updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
            return response?.data;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to update variant stock details.",
                icon: "error",
                timer: 3000,
                showConfirmButton: false,
            });
            return error;
        }
    };
    const updateMultipleVariantStock = async (variants) => {
        try {
            const response = await Apis.updateMultipleVariantStock({ variants });
            if (isOk(response)) {
                Swal.fire({
                    title: "Success!",
                    text: response?.data?.message || "All variant stock statuses updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
            return response?.data;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to update variant stock statuses.",
                icon: "error",
                timer: 2000,
                showConfirmButton: false,
            });
            return error;
        }
    };

    const updateProductPrimaryImage = async (imageId) => {
        try {
            const response = await Apis.updateProductPrimaryImage(imageId);
            return response;
        } catch (error) {
            console.error("Failed to update product primary image:", error);
            throw error;
        }
    };

    return { getDiscountTiers, addDiscountTiers, delteDiscountTiers, showOnHomeScreen, getProductData, addStock, showProductOnHomeScreen, getCategories, updateVariantStock, updateVariantStockDetails, updateMultipleVariantStock, updateProductPrimaryImage };
};

// --- Hook from Quotation/useHook.js ---
export const useQuotation = () => {
    const getProducts = async (setProductList) => {
        try {
            const response = await Apis.getAllProducts();
            const payload = response?.data?.data;
            setProductList(payload?.products || response?.data?.products || []);
        } catch (error) {
            console.error(error);
            setProductList([]);
        }
    };
    const generatePdf = async (data, setPdfFileLink) => {
        try {
            const response = await Apis.generatePdf(data);
            const fileUrl =
                response?.data?.data?.fileUrl || response?.data?.fileUrl;
            if (setPdfFileLink && fileUrl) setPdfFileLink(fileUrl);
            return fileUrl;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getVarinatByProductId = async (productId) => {
        try {
            const response = await Apis.getVarinatByProductId(productId);
            const payload = response?.data?.data || response?.data || {};
            return { variants: payload.variants || [] };
        } catch (error) {
            console.error(error);
            return { variants: [] };
        }
    };
    const getColorsByVariantId = async (variantId) => {
        try {
            const response = await Apis.getColorsByVariantId(variantId);
            const payload = response?.data?.data || response?.data || {};
            return { colors: payload.colors || [] };
        } catch (error) {
            console.error(error);
            return { colors: [] };
        }
    };
    return {
        getProducts,
        generatePdf,
        getVarinatByProductId,
        getColorsByVariantId,
    };
};

// --- Hook from Reviews/useHook.js ---
export const useReviews = () => {
    const getReviews = async (status, setReviewData, offset = 0, limit = 9) => {
        try {
            const params = {
                status,
                offset,
                limit
            };
            const response = await Apis.getReviews(params);
            const payload = response?.data?.data || {};
            const reviews = payload.reviews || (Array.isArray(payload) ? payload : []);
            // Keep { data, pagination } shape expected by Manage Reviews UI
            const shaped = {
                data: reviews,
                pagination: payload.pagination || {
                    totalRecords: reviews.length,
                    totalPages: 1,
                    currentPage: 1,
                    perPage: limit,
                },
            };
            setReviewData(shaped);
            return shaped;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const acceptReviews = async (body) => {
        try {
            const response = await Apis.acceptReviews(body);
            return response?.data || response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return { getReviews, acceptReviews };
};

// --- Combined Hook for Orders ---
export const useOrders = () => {
    const getORders = async (setOrderData, params = {}, offset = 0, limit = 9) => {
        try {
            const requestParams = {
                ...params,
                offset,
                limit
            };
            const response = await Apis.getOrders(requestParams);
            const payload = response?.data?.data || response?.data || {};
            const ordersData = payload.ordersData || [];
            setOrderData(ordersData);
            return payload;
        } catch (error) {
            console.error("Error fetching orders:", error);
            throw error;
        }
    };

    const updateOrderStatus = async (orderId, status) => {
        try {
            const body = { status };
            const response = await Apis.updateOrderStatus(orderId, body);
            return response?.data || response;
        } catch (error) {
            console.error("Error updating order status:", error);
            throw error;
        }
    };

    const getOrderByOrderId = async (orderId, setOrderData) => {
        try {
            const response = await Apis.getOrderByOrderId(orderId);
            const payload = response?.data?.data || response?.data || {};
            const orderData = payload.orderData || payload;
            setOrderData(orderData);
            return orderData;
        } catch (error) {
            console.error("Error fetching order data:", error);
            throw error;
        }
    };

    return { getORders, updateOrderStatus, getOrderByOrderId };
};
